import { getDb, PREFS_KEY } from '../db'
import { normalizeStoredNote } from '../repositories/personal'
import { SCHEMA_VERSION, type AppPreferences, type TechniqueNote } from '../schema'
import type { ExportPayload } from './validate'
import type { LegacyPlainNote } from '../notes/document'

export type LocalDataCounts = {
  referenceCount: number
  segmentCount: number
  noteCount: number
  favouriteCount: number
  performanceCount: number
}

/** Build a schema-versioned export of all personal local data. */
export async function buildExportPayload(): Promise<ExportPayload> {
  const db = await getDb()
  const [references, notes, favourites, performances, prefsRow] = await Promise.all([
    db.getAll('references'),
    db.getAll('notes'),
    db.getAll('favourites'),
    db.getAll('performances'),
    db.get('preferences', PREFS_KEY),
  ])

  const preferences: AppPreferences = prefsRow
    ? {
        lastKataId: prefsRow.lastKataId,
        lastTechniqueId: prefsRow.lastTechniqueId,
        deferYoutubeLoad: prefsRow.deferYoutubeLoad,
      }
    : {}

  const normalizedNotes: TechniqueNote[] = notes.map((note) =>
    normalizeStoredNote(note as TechniqueNote | LegacyPlainNote),
  )

  return {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    references,
    notes: normalizedNotes,
    favourites,
    preferences,
    performances,
  }
}

export async function countLocalPersonalData(): Promise<LocalDataCounts> {
  const db = await getDb()
  const references = await db.getAll('references')
  const notes = await db.getAll('notes')
  const favourites = await db.getAll('favourites')
  const performances = await db.getAll('performances')
  return {
    referenceCount: references.length,
    segmentCount: references.reduce((n, r) => n + r.segments.length, 0),
    noteCount: notes.length,
    favouriteCount: favourites.length,
    performanceCount: performances.length,
  }
}

/**
 * Replace-all import applied in one IndexedDB transaction.
 * On failure the transaction aborts and prior data remains.
 */
export async function applyImportReplaceAll(payload: ExportPayload): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(
    ['references', 'notes', 'favourites', 'performances', 'preferences'],
    'readwrite',
  )

  await tx.objectStore('references').clear()
  await tx.objectStore('notes').clear()
  await tx.objectStore('favourites').clear()
  await tx.objectStore('performances').clear()
  await tx.objectStore('preferences').clear()

  for (const ref of payload.references) {
    await tx.objectStore('references').put(ref)
  }
  for (const note of payload.notes) {
    await tx.objectStore('notes').put(note)
  }
  for (const fav of payload.favourites) {
    await tx.objectStore('favourites').put(fav)
  }
  for (const perf of payload.performances ?? []) {
    await tx.objectStore('performances').put(perf)
  }
  await tx.objectStore('preferences').put({ id: PREFS_KEY, ...payload.preferences })

  await tx.done
}

/**
 * Wipe personal local data (references, notes, favourites, performances, preferences).
 * Catalog is never touched. Caller must confirm in UI first.
 */
export async function resetLocalPersonalData(): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(
    ['references', 'notes', 'favourites', 'performances', 'preferences'],
    'readwrite',
  )
  await tx.objectStore('references').clear()
  await tx.objectStore('notes').clear()
  await tx.objectStore('favourites').clear()
  await tx.objectStore('performances').clear()
  await tx.objectStore('preferences').clear()
  await tx.done
}

export function exportPayloadToJson(payload: ExportPayload): string {
  return `${JSON.stringify(payload, null, 2)}\n`
}

export function downloadExportJson(payload: ExportPayload, filename?: string): void {
  const text = exportPayloadToJson(payload)
  const blob = new Blob([text], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const stamp = payload.exportedAt.slice(0, 10)
  a.href = url
  a.download = filename ?? `fs-judo-kata-backup-${stamp}.json`
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
